import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  TextField,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Breadcrumbs,
  Link,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { QueryableFieldsQuery } from '../../../../types/generated-types';

interface SelectionPathBuilderProps {
  value: string;
  onChange: (value: string) => void;
  targetModel: string;
  fieldsData: QueryableFieldsQuery['queryableFields'];
}

export const SelectionPathBuilder: React.FC<SelectionPathBuilderProps> = ({
  value,
  onChange,
  targetModel,
  fieldsData,
}: SelectionPathBuilderProps) => {
  // Path builder dialog state
  const [pathDialogOpen, setPathDialogOpen] = useState(false);
  const [pathStack, setPathStack] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Focus search field when dialog opens or pathStack changes
  useEffect(() => {
    if (pathDialogOpen) {
      const raf = requestAnimationFrame(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
          searchInputRef.current.select();
        }
      });
      return () => cancelAnimationFrame(raf);
    }
  }, [pathDialogOpen, pathStack]);

  // Helper function to format the path for display (preserves original case)
  const formatPathForDisplay = (path: string): string => {
    if (!path) return '';
    // Split by dots and capitalize each segment
    return path
      .split('.')
      .map((segment) => {
        // Handle camelCase by inserting spaces before capital letters
        return segment
          .replace(/([A-Z])/g, ' $1')
          .replace(/^./, (str) => str.toUpperCase())
          .trim();
      })
      .join(' > ');
  };

  const displayPath = useMemo(
    () => (value ? formatPathForDisplay(value) : ''),
    [value],
  );

  // Helper to get the current fields at the current path
  const getCurrentFields = () => {
    if (!fieldsData) return {};
    let current = fieldsData;
    for (const part of pathStack) {
      if (current[part] && typeof current[part] === 'object') {
        current = current[part];
      } else {
        return {};
      }
    }
    return current;
  };

  // Helper to determine if a field is a leaf type of field (not an object)
  const isLeafField = (value: any) => {
    if (typeof value === 'string') {
      // If the value is a string, it's a field type
      return true;
    }
    return !!Array.isArray(value);
  };

  // Filtered field list based on search
  const getFilteredFieldEntries = (fields: Record<string, any>) => {
    const entries = Object.entries(fields);
    if (!search) return entries;
    const lower = search.toLowerCase();
    return entries.filter(([field]) => field.toLowerCase().includes(lower));
  };

  const onClose = () => setPathDialogOpen(false);

  // Path builder dialog UI
  const renderPathBuilderDialog = () => {
    const currentFields = getCurrentFields();
    const filteredEntries = getFilteredFieldEntries(currentFields);
    return (
      <Dialog open={pathDialogOpen} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ p: 0 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 3,
              py: 2,
            }}
          >
            <Typography variant="h6" sx={{ flex: 1 }}>
              Select Field Path
            </Typography>
            <TextField
              inputRef={searchInputRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search fields"
              size="small"
              sx={{ width: 200, ml: 2 }}
              InputProps={{
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <ClearIcon
                      sx={{ cursor: 'pointer' }}
                      onClick={() => setSearch('')}
                    />
                  </InputAdornment>
                ) : null,
              }}
            />
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 0.5, pb: 1, px: 1 }}>
          <Breadcrumbs sx={{ mb: 0.25 }}>
            <Link
              underline="hover"
              color="inherit"
              onClick={() => {
                setPathStack([]);
                setSearch('');
              }}
              sx={{ cursor: 'pointer' }}
            >
              {targetModel}
            </Link>
            {pathStack.map((part, idx) => (
              <Link
                key={idx}
                underline="hover"
                color="inherit"
                onClick={() => {
                  setPathStack(pathStack.slice(0, idx + 1));
                  setSearch('');
                }}
                sx={{ cursor: 'pointer' }}
              >
                {formatPathForDisplay(part)}
              </Link>
            ))}
          </Breadcrumbs>
          <List dense sx={{ pt: 0 }}>
            {filteredEntries
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([field, value]) => (
                <ListItemButton
                  dense
                  key={field}
                  sx={{
                    minHeight: 24,
                    py: 0.25,
                    px: 1,
                  }}
                  onClick={() => {
                    if (isLeafField(value) || Array.isArray(value)) {
                      // Set the path and close
                      const fullPath = [...pathStack, field].join('.');
                      setPathDialogOpen(false);
                      // Use setTimeout to ensure dialog is closed before onChange is called
                      setTimeout(() => {
                        onChange(fullPath);
                      }, 0);
                    } else {
                      // Drill down
                      setPathStack([...pathStack, field]);
                      setSearch('');
                      setTimeout(() => {
                        if (searchInputRef.current)
                          searchInputRef.current.focus();
                      }, 0);
                    }
                  }}
                >
                  <ListItemText
                    primary={
                      <span>
                        {field}
                        <span
                          style={{
                            fontSize: '0.85em',
                            color: '#666',
                            marginLeft: 4,
                          }}
                        >
                          {Array.isArray(value)
                            ? '(String)'
                            : isLeafField(value)
                              ? `(${value as string})`
                              : (() => {
                                  const metricFields = [
                                    'id',
                                    'previousId',
                                    'timestamp',
                                    'units',
                                    'updatePending',
                                    'value',
                                  ].sort();
                                  const childKeys = Object.keys(
                                    value as Record<string, unknown>,
                                  ).sort();
                                  const isMetric =
                                    childKeys.length === metricFields.length &&
                                    childKeys.every(
                                      (k, i) => k === metricFields[i],
                                    );
                                  return `(${isMetric ? 'Metric' : 'Object'}) >`;
                                })()}
                        </span>
                      </span>
                    }
                    sx={{ my: 0 }}
                  />
                </ListItemButton>
              ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPathDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Box sx={{ minWidth: 200 }}>
      <Button
        variant="outlined"
        fullWidth
        onClick={() => {
          setPathDialogOpen(true);
          setPathStack(value ? value.split('.') : []);
        }}
      >
        {value ? displayPath : 'Select Field'}
      </Button>
      {renderPathBuilderDialog()}
    </Box>
  );
};
